import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Mic,
  Sparkles,
  Loader2,
  TrendingUp,
  CheckCircle2,
  Lightbulb,
  ArrowLeft,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import LemonBotPanel from "@/components/LemonBotPanel";
import MicButton from "@/components/accessibility/MicButton";
import ReadAloudButton from "@/components/accessibility/ReadAloudButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RUBRIC_LABELS = {
  clarity: "Clarity",
  confidence: "Confidence",
  problem_solution: "Problem & Solution",
  customer: "Customer",
  pricing: "Pricing",
  call_to_action: "Call to Action",
};

function authHeaders() {
  const token =
    localStorage.getItem("token") || localStorage.getItem("student_token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

function ScoreBar({ value, max = 10 }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const color =
    pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-400" : "bg-gray-300";
  return (
    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full transition-all duration-500 ${color}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function RubricCard({ result }) {
  if (!result) return null;
  const tierStyle =
    result.tier === "ready"
      ? "bg-emerald-100 text-emerald-800"
      : result.tier === "almost"
      ? "bg-amber-100 text-amber-800"
      : "bg-gray-100 text-gray-700";
  return (
    <div
      className="bg-white border border-amber-200 rounded-2xl p-5 shadow-sm"
      data-testid="pitch-result-card"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-amber-500" />
          <h3 className="font-bold text-gray-900">Your Pitch Score</h3>
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tierStyle} capitalize`}
          >
            {result.tier}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <ReadAloudButton
            testId="pitch-result-read-aloud"
            inline
            cta="Listen to feedback"
            text={[
              `Your pitch scored ${result.percent} percent — tier ${result.tier}.`,
              ...(result.strengths || []).map((s) => `Strength: ${s}.`),
              result.improvement ? `One thing to improve: ${result.improvement}.` : "",
            ].filter(Boolean).join(" ")}
          />
          <div className="text-right">
            <div
              className="text-3xl font-bold tabular-nums text-amber-700"
              data-testid="pitch-result-percent"
              aria-label={`Score ${result.percent} percent — ${result.total} out of ${result.max_total}`}
            >
              {result.percent}%
            </div>
            <div className="text-xs text-gray-500">
              {result.total}/{result.max_total}
            </div>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-x-5 gap-y-2.5 mb-4">
        {Object.entries(result.scores).map(([key, val]) => (
          <div key={key}>
            <div className="flex justify-between text-xs mb-0.5">
              <span className="text-gray-700">{RUBRIC_LABELS[key] || key}</span>
              <span className="font-semibold text-gray-800 tabular-nums">
                {val}/10
              </span>
            </div>
            <ScoreBar value={val} />
          </div>
        ))}
      </div>

      {result.strengths && result.strengths.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-2">
          <div className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-1.5 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Strengths
          </div>
          <ul className="space-y-1">
            {result.strengths.map((s, i) => (
              <li key={i} className="text-sm text-emerald-900">
                • {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.improvement && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-1.5 flex items-center gap-1">
            <Lightbulb className="w-3.5 h-3.5" />
            One thing to improve
          </div>
          <p className="text-sm text-amber-900">{result.improvement}</p>
        </div>
      )}
    </div>
  );
}

function PitchPracticePage({ user, onLogout }) {
  const navigate = useNavigate();
  const [pitch, setPitch] = useState("");
  const [scoring, setScoring] = useState(false);
  const [scoringStep, setScoringStep] = useState(0);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);

  const SCORING_STEPS = [
    { tip: "Reading your pitch line by line…", icon: "📖" },
    { tip: "Checking clarity and confidence…", icon: "🔎" },
    { tip: "Looking at your problem and solution…", icon: "💡" },
    { tip: "Calculating your customer fit…", icon: "👥" },
    { tip: "Drafting your improvement step…", icon: "✏️" },
  ];

  // Rotate the tip every 1.4s while scoring
  useEffect(() => {
    if (!scoring) return;
    setScoringStep(0);
    const id = setInterval(() => {
      setScoringStep((s) => (s + 1) % SCORING_STEPS.length);
    }, 1400);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoring]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/lemonbot/pitch-attempts/me`,
        authHeaders()
      );
      setHistory(res.data?.attempts || []);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleScore = async () => {
    if (!pitch.trim() || scoring) return;
    setScoring(true);
    setError(null);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/lemonbot/pitch-score`,
        { pitch_text: pitch.trim() },
        authHeaders()
      );
      setResult(res.data);
      fetchHistory();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not score your pitch.");
    } finally {
      setScoring(false);
    }
  };

  const wordCount = pitch.trim() ? pitch.trim().split(/\s+/).length : 0;
  const bestPct = history.length
    ? Math.max(...history.map((h) => h.percent || 0))
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      <Navbar user={user} onLogout={onLogout} />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          data-testid="pitch-back-btn"
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-6 text-white shadow-lg mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Mic className="w-6 h-6" />
            <h1 className="text-2xl font-bold">Pitch Practice</h1>
          </div>
          <p className="text-white/90 text-sm max-w-xl">
            Type your 30-second pitch below. LemonBot will score it on six
            dimensions and give you ONE concrete thing to improve. Every
            attempt is saved so you can see yourself level up.
          </p>
        </div>

        {/* Input card */}
        <div className="bg-white border border-amber-200 rounded-2xl p-5 shadow-sm mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
              Your Pitch
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 rounded-full px-2 py-0.5"
                title="LemonBot scores this with the 6-dimension rubric"
              >
                <Sparkles className="w-3 h-3" />
                AI Assist
              </span>
            </label>
            <div className="text-xs text-gray-500">
              {wordCount} {wordCount === 1 ? "word" : "words"} · aim for 60-90
            </div>
          </div>
          <textarea
            id="pitch-input"
            value={pitch}
            onChange={(e) => setPitch(e.target.value)}
            rows={6}
            placeholder="Hi, I'm Jordan and I run Lemon Sparks — handcrafted real-fruit lemonade for middle schoolers tired of sugary sodas..."
            data-testid="pitch-input"
            className="w-full rounded-lg border-2 border-amber-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-200 focus:outline-none px-4 py-3 text-sm resize-vertical"
          />
          <div className="flex items-center justify-between mt-2 mb-1">
            <span className="text-[11px] text-gray-500">
              Voice typing turns your speech into text in the box. Edit before scoring.
            </span>
            <MicButton
              inputId="pitch-input"
              testId="pitch-mic-btn"
              size="sm"
              onTranscript={(t) => setPitch((v) => (v ? v + " " : "") + t.trim())}
            />
          </div>
          <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
            <div className="text-xs text-gray-500">
              Hook → Problem & Solution → Customer → Price → Ask
            </div>
            <button
              onClick={handleScore}
              disabled={scoring || pitch.trim().length < 20}
              data-testid="pitch-score-btn"
              className="inline-flex items-center gap-2 bg-gradient-to-br from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-5 py-2.5 rounded-lg shadow"
            >
              {scoring ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              {scoring ? "LemonBot is scoring…" : "Score My Pitch"}
            </button>
          </div>
          {error && (
            <p className="mt-2 text-sm text-red-600" data-testid="pitch-error">
              {error}
            </p>
          )}
        </div>

        {/* Loading skeleton with rotating tips while scoring */}
        {scoring && (
          <div
            className="bg-white border border-amber-200 rounded-2xl p-5 shadow-sm mb-6 animate-pulse-soft"
            data-testid="pitch-scoring-skeleton"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center text-xl flex-shrink-0">
                {SCORING_STEPS[scoringStep].icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900">
                  Analyzing your pitch…
                </div>
                <div
                  className="text-sm text-gray-600 transition-opacity"
                  key={scoringStep}
                  data-testid="pitch-scoring-tip"
                >
                  {SCORING_STEPS[scoringStep].tip}
                </div>
              </div>
              <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
            </div>
            {/* 6 placeholder rubric bars */}
            <div className="grid sm:grid-cols-2 gap-x-5 gap-y-2.5">
              {Object.values(RUBRIC_LABELS).map((label, i) => (
                <div key={label}>
                  <div className="flex justify-between text-xs mb-0.5">
                    <span className="text-gray-400">{label}</span>
                    <span className="text-gray-300">…</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-200 to-orange-200 animate-pulse"
                      style={{ width: `${30 + i * 8}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Result */}
        {result && !scoring && (
          <div className="mb-6">
            <RubricCard result={result} />
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div
            className="bg-white border border-gray-200 rounded-2xl p-5 mb-6"
            data-testid="pitch-history"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900">Your Progress</h3>
              {bestPct !== null && (
                <span className="text-xs font-semibold bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">
                  Best: {bestPct}%
                </span>
              )}
            </div>
            <ul className="text-sm text-gray-700 divide-y divide-gray-100">
              {history.slice(-8).reverse().map((h, idx) => (
                <li key={idx} className="flex justify-between py-1.5">
                  <span className="text-gray-500">
                    {new Date(h.created_at).toLocaleString()}
                  </span>
                  <span className="font-semibold tabular-nums text-amber-700">
                    {h.percent}%
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Inline LemonBot coach (free-form questions about pitching) */}
        <LemonBotPanel
          activity="pitch_practice"
          title="LemonBot · Pitch coach"
          subtitle="I'll help you tighten your pitch — try a chip below"
          openByDefault
          quickActions={[
            {
              intent: "pitch_practice",
              label: "Give me a template",
              prompt:
                "Give me a 30-second pitch template I can fill in (Hook / Problem-Solution / Ask). Keep it under 80 words.",
            },
            {
              intent: "give_example",
              label: "Show example",
              prompt:
                "Give me ONE strong 30-second pitch example for a student lemonade business. Keep it under 80 words.",
            },
            {
              intent: "explain_easier",
              label: "I'm nervous",
              prompt:
                "I'm nervous about pitching. Give me ONE 5-minute calming exercise plus ONE thing I can practice right now to feel more confident.",
            },
          ]}
        />
      </div>
    </div>
  );
}

export default PitchPracticePage;
